import {
  forwardRef,
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useImperativeHandle,
  type FormEvent,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Paperclip, X, FileText } from "lucide-react";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { Button } from "./Button";
import { TextArea } from "./TextArea";

const ACCEPTED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
].join(",");

const MAX_FILES = 5;

const Root = styled.form`
  padding: ${spacing.x2}px ${spacing.x3}px;
  border-top: 1px solid ${colors.border};
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
  flex-shrink: 0;
  background-color: ${colors.background};
`;

const AttachmentStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.x1}px;
`;

const AttachmentThumb = styled.div`
  position: relative;
  width: 56px;
  height: 56px;
  border-radius: ${borderRadius.small}px;
  overflow: hidden;
  border: 1px solid ${colors.border};
  flex-shrink: 0;
`;

const ThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const RemoveThumbButton = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
`;

const FilePill = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
  padding: 0 ${spacing.x1}px 0 0;
  height: 32px;
  border-radius: ${borderRadius.small}px;
  border: 1px solid ${colors.border};
  background-color: ${colors.backgroundDark};
  font-size: ${fontSizes.small}px;
  color: ${colors.text};
  max-width: 200px;
`;

const FilePillName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
`;

const RemovePillButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${colors.textMuted};
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  &:hover {
    color: ${colors.text};
  }
`;

const InputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${spacing.x1}px;
`;

const TextAreaWrap = styled.div`
  flex: 1;
  min-width: 0;
`;

const AttachButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.small}px;
  color: ${colors.textMuted};
  cursor: pointer;
  padding: ${spacing.x1}px;
  flex-shrink: 0;
  &:hover:not(:disabled) {
    color: ${colors.text};
    background-color: ${colors.backgroundDark};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
  align-self: flex-end;
`;

const InlineError = styled.div`
  font-size: ${fontSizes.small}px;
  color: ${colors.danger};
`;

const isImageFile = (file: File) => file.type.startsWith("image/");

type Props = {
  onSend: (text: string, files: File[]) => void;
  isSending: boolean;
  sendError: Error | null;
};

export type ChatComposerHandle = {
  addFiles: (incoming: File[]) => void;
};

export const ChatComposer = forwardRef<ChatComposerHandle, Props>(function ChatComposer(
  { onSend, isSending, sendError },
  ref,
) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const previews = useMemo(
    () => files.map((file) => ({ file, url: isImageFile(file) ? URL.createObjectURL(file) : null })),
    [files],
  );

  useEffect(() => {
    return () => {
      previews.forEach((p) => { if (p.url) URL.revokeObjectURL(p.url); });
    };
  }, [previews]);

  const submit = () => {
    const trimmed = text.trim();
    if ((!trimmed && files.length === 0) || isSending) return;
    onSend(trimmed, files);
    setText("");
    setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const addFiles = useCallback(
    (incoming: File[]) => {
      if (isSending || incoming.length === 0) return;
      setFiles((prev) => [...prev, ...incoming].slice(0, MAX_FILES));
    },
    [isSending]
  );

  useImperativeHandle(ref, () => ({ addFiles }), [addFiles]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files ?? []));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <Root onSubmit={handleSubmit}>
      {previews.length > 0 && (
        <AttachmentStrip>
          {previews.map((p, idx) =>
            p.url ? (
              <AttachmentThumb key={idx}>
                <ThumbImg src={p.url} alt={p.file.name} />
                <RemoveThumbButton type="button" onClick={() => removeFile(idx)}>
                  <X size={10} />
                </RemoveThumbButton>
              </AttachmentThumb>
            ) : (
              <FilePill key={idx}>
                <FileText size={12} />
                <FilePillName>{p.file.name}</FilePillName>
                <RemovePillButton type="button" onClick={() => removeFile(idx)}>
                  <X size={12} />
                </RemovePillButton>
              </FilePill>
            ),
          )}
        </AttachmentStrip>
      )}
      <InputRow>
        <TextAreaWrap>
          <TextArea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("storeChatSessions.replyPlaceholder")}
            rows={3}
            disabled={isSending}
          />
        </TextAreaWrap>
        <ButtonGroup>
          <AttachButton
            type="button"
            disabled={isSending || files.length >= MAX_FILES}
            onClick={() => fileInputRef.current?.click()}
            title={`${t("storeChatSessions.attachFile")} (max ${MAX_FILES})`}
          >
            <Paperclip size={16} />
          </AttachButton>
          <Button type="submit" disabled={isSending || (!text.trim() && files.length === 0)}>
            {isSending ? t("storeChatSessions.sending") : t("storeChatSessions.send")}
          </Button>
        </ButtonGroup>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          multiple
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </InputRow>
      {sendError && (
        <InlineError>
          {sendError instanceof Error ? sendError.message : t("storeChatSessions.sendFailed")}
        </InlineError>
      )}
    </Root>
  );
});
